import React, { useCallback, useState } from "react";
import { DashboardFilters, DashboardFiltersBar } from "../../components/DashboardFiltersBar";
import { AutocompleteInput } from "../../components/AutocompleteInput";
import { useAuth } from "../../auth/AuthContext";
import { PageState } from "../../components/PageState";
import { usePagedQuery } from "../../hooks/usePagedQuery";
import { formatDateBR, formatMoneyBR } from "../../utils/format";

type OpenFinanceTransaction = {
  id: number;
  provider: string;
  externalId: string;
  occurredAt: string;
  amount: number;
  descriptionRaw?: string | null;
  status: string;
  accountExternalId?: string | null;
  suggestions?: Suggestion[];
};

type Suggestion = {
  movementId: number;
  score: number;
  reasons: string[];
  movement: {
    description: string;
    occurredAt: string;
    amount: number;
    bankAccountId: number;
  };
};

type SuggestionsResponse = {
  data: Array<{
    transaction: OpenFinanceTransaction;
    suggestions: Suggestion[];
  }>;
};

type Option = { id: number; label: string };

export const OpenFinanceReconciliationPage: React.FC = () => {
  const { client } = useAuth();
  const [filters, setFilters] = useState<DashboardFilters>({});
  const [provider, setProvider] = useState("");
  const [status, setStatus] = useState("NEW");
  const {
    rows,
    total,
    loading,
    error,
    page,
    take,
    setPage,
    q,
    setQ,
    reload,
  } = usePagedQuery<OpenFinanceTransaction>({
    endpoint: "/openfinance/reconciliation/queue",
    filters: {
      entityId: filters.entityId,
      from: filters.from,
      to: filters.to,
      status: status || undefined,
      provider: provider || undefined,
    },
  });
  const [selected, setSelected] = useState<OpenFinanceTransaction | null>(null);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [movementId, setMovementId] = useState("");
  const [categoryOption, setCategoryOption] = useState<Option | null>(null);
  const [costCenterOption, setCostCenterOption] = useState<Option | null>(null);
  const [accountOption, setAccountOption] = useState<Option | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [modalType, setModalType] = useState<"link" | "create" | null>(null);

  const fetchSuggestions = async (txId: number) => {
    if (!filters.entityId) {
      return;
    }
    const params = new URLSearchParams();
    params.set("entityId", String(filters.entityId));
    params.set("txId", String(txId));
    params.set("take", "1");
    params.set("skip", "0");
    const response = await client.get(`/reconciliation/suggestions?${params.toString()}`);
    if (!response.ok) {
      setSuggestions([]);
      return;
    }
    const payload = (await response.json()) as SuggestionsResponse;
    setSuggestions(payload.data[0]?.suggestions ?? []);
  };

  const handleSelect = async (item: OpenFinanceTransaction) => {
    setSelected(item);
    setActionMessage(null);
    setMovementId("");
    setCategoryOption(null);
    setCostCenterOption(null);
    setAccountOption(null);
    if (item.suggestions) {
      setSuggestions(item.suggestions);
      return;
    }
    await fetchSuggestions(item.id);
  };

  const handleLink = async () => {
    if (!selected || !movementId || !filters.entityId) {
      setActionMessage("Informe o Movement ID.");
      return;
    }
    const response = await client.post(
      `/openfinance/reconciliation/${selected.id}/link-movement?entityId=${filters.entityId}`,
      { movementId: Number(movementId) }
    );
    if (!response.ok) {
      setActionMessage("Falha ao vincular movimento.");
      return;
    }
    setActionMessage("Movimento vinculado.");
    await reload();
    setModalType(null);
  };

  const handleCreateMovement = async () => {
    if (!selected || !filters.entityId) {
      return;
    }
    const response = await client.post(
      `/openfinance/reconciliation/${selected.id}/create-movement?entityId=${filters.entityId}`,
      {
        categoryId: categoryOption?.id,
        costCenterId: costCenterOption?.id,
        accountId: accountOption?.id,
      }
    );
    if (!response.ok) {
      setActionMessage("Falha ao criar movimento.");
      return;
    }
    setActionMessage("Movimento criado.");
    await reload();
    setModalType(null);
  };

  const handleIgnore = async () => {
    if (!selected || !filters.entityId) {
      return;
    }
    const response = await client.post(
      `/openfinance/reconciliation/${selected.id}/ignore?entityId=${filters.entityId}`,
      {}
    );
    if (!response.ok) {
      setActionMessage("Falha ao ignorar.");
      return;
    }
    setActionMessage("Transacao ignorada.");
    await reload();
  };

  const handleReprocess = async () => {
    if (!filters.entityId) {
      setActionMessage("Selecione a entidade.");
      return;
    }
    const response = await client.post("/openfinance/reconciliation/reprocess", {
      entityId: filters.entityId,
      from: filters.from,
      to: filters.to,
    });
    if (!response.ok) {
      setActionMessage("Falha ao reprocessar.");
      return;
    }
    setActionMessage("Reprocessamento iniciado.");
  };

  const fetchCategories = useCallback(
    async (text: string) => {
      const response = await client.get(
        `/modules/categories?q=${encodeURIComponent(text)}&take=10&skip=0`
      );
      if (!response.ok) {
        return [];
      }
      const payload = (await response.json()) as { data: Array<{ id: number; name: string }> };
      return payload.data.map((item) => ({ id: item.id, label: item.name }));
    },
    [client]
  );

  const fetchCostCenters = useCallback(
    async (text: string) => {
      const response = await client.get(
        `/modules/cost-centers?q=${encodeURIComponent(text)}&take=10&skip=0`
      );
      if (!response.ok) {
        return [];
      }
      const payload = (await response.json()) as { data: Array<{ id: number; name: string; code?: string | null }> };
      return payload.data.map((item) => ({
        id: item.id,
        label: `${item.code ?? ""} ${item.name}`.trim(),
      }));
    },
    [client]
  );

  const fetchAccounts = useCallback(
    async (text: string) => {
      const response = await client.get(
        `/modules/accounts?q=${encodeURIComponent(text)}&take=10&skip=0`
      );
      if (!response.ok) {
        return [];
      }
      const payload = (await response.json()) as { data: Array<{ id: number; code: string; name: string }> };
      return payload.data.map((item) => ({
        id: item.id,
        label: `${item.code} - ${item.name}`,
      }));
    },
    [client]
  );

  const paginationText = React.useMemo(() => {
    if (total === 0) {
      return "0 resultados";
    }
    const start = (page - 1) * take + 1;
    const end = Math.min(page * take, total);
    return `${start} - ${end} de ${total}`;
  }, [page, take, total]);

  return (
    <div className="container py-4">
      <h1 className="mb-3">Conciliacao Open Finance</h1>
      <DashboardFiltersBar value={filters} onChange={setFilters} />

      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label">Provider</label>
              <select
                className="form-select"
                value={provider}
                onChange={(event) => setProvider(event.target.value)}
              >
                <option value="">Todos</option>
                <option value="MERCADO_PAGO">Mercado Pago</option>
                <option value="BTG">BTG</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={status}
                onChange={(event) => setStatus(event.target.value)}
              >
                <option value="NEW">Novo</option>
                <option value="IMPORTED">Importado</option>
                <option value="ERROR">Erro</option>
                <option value="CONCILIATED">Conciliado</option>
                <option value="IGNORED">Ignorado</option>
              </select>
            </div>
            <div className="col-md-5">
              <label className="form-label">Busca</label>
              <input
                className="form-control"
                value={q}
                placeholder="Buscar por descricao ou externalId"
                onChange={(event) => setQ(event.target.value)}
              />
            </div>
            <div className="col-md-1 text-end">
              <button className="btn btn-outline-primary" onClick={handleReprocess}>
                Reprocessar
              </button>
            </div>
          </div>
          {actionMessage && <div className="alert alert-info mt-3">{actionMessage}</div>}
        </div>
      </div>

      <div className="row g-4">
        <div className="col-lg-7">
          <div className="card shadow-sm">
            <div className="card-body">
              <PageState loading={loading} error={error}>
                <div className="table-responsive">
                  <table className="table table-sm align-middle">
                    <thead>
                      <tr>
                        <th>Data</th>
                        <th>Conta</th>
                        <th>Descricao</th>
                        <th>Valor</th>
                        <th>Sugestao</th>
                        <th>Status</th>
                        <th>Acoes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((item) => {
                        const suggestion = item.suggestions?.[0];
                        return (
                          <tr key={item.id}>
                            <td>{formatDateBR(item.occurredAt)}</td>
                            <td>{item.accountExternalId ?? "-"}</td>
                            <td>{item.descriptionRaw ?? "-"}</td>
                            <td>{formatMoneyBR(item.amount)}</td>
                            <td>
                              {suggestion ? (
                                <div className="small">
                                  <div>Score {suggestion.score}</div>
                                  <div className="text-muted">
                                    {suggestion.reasons.join(", ")}
                                  </div>
                                </div>
                              ) : (
                                "-"
                              )}
                            </td>
                            <td>{item.status}</td>
                            <td>
                              <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => handleSelect(item)}
                              >
                                Revisar
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                      {rows.length === 0 && (
                        <tr>
                          <td colSpan={7} className="text-center">
                            Nenhum registro encontrado.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </PageState>
              <div className="d-flex justify-content-between align-items-center">
                <small className="text-muted">{paginationText}</small>
                <div className="btn-group">
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    disabled={page <= 1}
                    onClick={() => setPage(Math.max(1, page - 1))}
                  >
                    Anterior
                  </button>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    disabled={page * take >= total}
                    onClick={() => setPage(page + 1)}
                  >
                    Proximo
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-lg-5">
          <div className="card shadow-sm">
            <div className="card-body">
              <h5 className="card-title">Revisao</h5>
              {!selected && (
                <p className="text-muted">Selecione uma transacao para revisar.</p>
              )}
              {selected && (
                <>
                  <p className="mb-1">
                    <strong>Descricao:</strong> {selected.descriptionRaw ?? "-"}
                  </p>
                  <p className="mb-1">
                    <strong>Valor:</strong> {formatMoneyBR(selected.amount)}
                  </p>
                  <p className="mb-3">
                    <strong>Data:</strong> {formatDateBR(selected.occurredAt)}
                  </p>
                  <p className="mb-3">
                    <strong>Conta:</strong> {selected.accountExternalId ?? "-"}
                  </p>

                  <div className="mb-3 d-flex flex-wrap gap-2">
                    <button
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => setModalType("link")}
                    >
                      Vincular movimento existente
                    </button>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => setModalType("create")}
                    >
                      Criar movimento
                    </button>
                  </div>

                  <div className="mb-3">
                    <button className="btn btn-outline-danger btn-sm" onClick={handleIgnore}>
                      Ignorar transacao
                    </button>
                  </div>

                  <div>
                    <h6>Sugestoes</h6>
                    {suggestions.length === 0 && (
                      <p className="text-muted">Sem sugestoes.</p>
                    )}
                    <ul className="list-unstyled">
                      {suggestions.map((suggestion) => (
                        <li key={suggestion.movementId} className="mb-2">
                          <div className="border rounded p-2">
                            <div>
                              <strong>ID:</strong> {suggestion.movementId} |{" "}
                              <strong>Score:</strong> {suggestion.score}
                            </div>
                            <div className="small text-muted">
                              {suggestion.reasons.join(", ")}
                            </div>
                            <div>{suggestion.movement.description}</div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {modalType && selected && (
        <div className="modal d-block" tabIndex={-1} role="dialog">
          <div className="modal-dialog modal-lg" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {modalType === "link" ? "Vincular movimento" : "Criar movimento"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setModalType(null)}
                />
              </div>
              <div className="modal-body">
                <p>
                  <strong>Descricao:</strong> {selected.descriptionRaw ?? "-"}
                </p>
                <p>
                  <strong>Valor:</strong> {formatMoneyBR(selected.amount)} |{" "}
                  <strong>Data:</strong> {formatDateBR(selected.occurredAt)}
                </p>
                {modalType === "link" ? (
                  <div>
                    <label className="form-label">Movement ID</label>
                    <input
                      className="form-control"
                      value={movementId}
                      onChange={(event) => setMovementId(event.target.value)}
                      placeholder="ID do movimento"
                    />
                  </div>
                ) : (
                  <>
                    <AutocompleteInput
                      label="Categoria"
                      placeholder="Buscar categoria"
                      value={categoryOption?.id ?? null}
                      selectedLabel={categoryOption?.label}
                      fetchOptions={fetchCategories}
                      onSelect={setCategoryOption}
                    />
                    <AutocompleteInput
                      label="Centro de custo"
                      placeholder="Buscar centro"
                      value={costCenterOption?.id ?? null}
                      selectedLabel={costCenterOption?.label}
                      fetchOptions={fetchCostCenters}
                      onSelect={setCostCenterOption}
                    />
                    <AutocompleteInput
                      label="Conta contabil"
                      placeholder="Buscar conta"
                      value={accountOption?.id ?? null}
                      selectedLabel={accountOption?.label}
                      fetchOptions={fetchAccounts}
                      onSelect={setAccountOption}
                    />
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => setModalType(null)}
                >
                  Cancelar
                </button>
                {modalType === "link" ? (
                  <button className="btn btn-primary" onClick={handleLink}>
                    Vincular
                  </button>
                ) : (
                  <button className="btn btn-primary" onClick={handleCreateMovement}>
                    Criar movimento
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
